import { useMemo } from "react"
import { DateTime } from "luxon"
import { isSlotWithinRange } from "../helpers/time_math"

interface BusyBlock {
  startTime: string
  endTime: string
}

interface UseTimeSlotsParams {
  slots: DateTime[]
  dragStartSlot: DateTime | null
  dragEndSlot: DateTime | null
  currentResStart: DateTime | null
  currentResEnd: DateTime | null
  busyBlocks: BusyBlock[]
  closedUntil?: DateTime | null
}

interface SelectionRange {
  startIndex: number
  endIndex: number
  endBoundaryIndex: number
  startTime: DateTime
  endTime: DateTime
}

export const useTimeSlots = ({
  slots,
  dragStartSlot,
  dragEndSlot,
  currentResStart,
  currentResEnd,
  busyBlocks,
  closedUntil,
}: UseTimeSlotsParams) => {
  const conflicts = useMemo(() => {
    let start: DateTime | null = null
    let end: DateTime | null = null

    if (dragStartSlot && dragEndSlot) {
      start = dragStartSlot < dragEndSlot ? dragStartSlot : dragEndSlot
      end = dragStartSlot < dragEndSlot ? dragEndSlot : dragStartSlot
    } else if (currentResStart && currentResEnd) {
      start = currentResStart
      end = currentResEnd
    }

    if (!start || !end) return []

    const conflictSlots: DateTime[] = []

    busyBlocks.forEach((block) => {
      const blockStart = DateTime.fromISO(block.startTime)
      const blockEnd = DateTime.fromISO(block.endTime)
      slots.forEach((slot) => {
        if (isSlotWithinRange(slot, start!, end!) && isSlotWithinRange(slot, blockStart, blockEnd)) {
          conflictSlots.push(slot)
        }
      })
    })

    if (closedUntil) {
      slots.forEach((slot) => {
        if (isSlotWithinRange(slot, start!, end!) && slot < closedUntil!) {
          conflictSlots.push(slot)
        }
      })
    }

    return conflictSlots
  }, [dragStartSlot, dragEndSlot, currentResStart, currentResEnd, busyBlocks, closedUntil, slots])

  const conflictRange = useMemo(() => {
    if (conflicts.length === 0) return null
    const sorted = [...conflicts].sort((a, b) => a.toMillis() - b.toMillis())
    const startIndex = slots.findIndex((s) => s.equals(sorted[0]))
    const endIndex = slots.findIndex((s) => s.equals(sorted[sorted.length - 1]))
    return { startIndex, endIndex }
  }, [conflicts, slots])

  const selectionRange = useMemo((): SelectionRange | null => {
    let start: DateTime | null = null
    let end: DateTime | null = null

    if (dragStartSlot && dragEndSlot) {
      start = dragStartSlot < dragEndSlot ? dragStartSlot : dragEndSlot
      end = dragStartSlot < dragEndSlot ? dragEndSlot : dragStartSlot
    } else if (currentResStart && currentResEnd) {
      start = currentResStart
      end = currentResEnd
    }

    if (!start || !end) return null

    const startIndex = slots.findIndex((s) => s.toMillis() === start!.toMillis())
    const endIndex = slots.findIndex((s) => s.toMillis() >= end!.toMillis())

    if (startIndex === -1) return null

    return {
      startIndex,
      endIndex: endIndex === -1 ? slots.length - 1 : endIndex,
      endBoundaryIndex: endIndex === -1 ? slots.length : endIndex,
      startTime: start,
      endTime: end,
    }
  }, [dragStartSlot, dragEndSlot, currentResStart, currentResEnd, slots])

  return { conflicts, conflictRange, selectionRange }
}
